import { __decorate } from "tslib";
import React, { Children } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import debounce from 'lodash/debounce';
import isString from 'lodash/isString';
import { computed, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import isPromise from 'is-promise';
import { ProgressType } from '../../../es/progress/enum';
import { getConfig } from '../../../es/configure';
import Icon from '../icon';
import FormContext from '../form/FormContext';
import Progress from '../progress';
import Ripple from '../ripple';
import { ButtonColor, ButtonType, FuncType } from './enum';
import { DataSetStatus } from '../data-set/enum';
import { Size, WaitType } from '../core/enum';
import DataSetComponent from '../data-set/DataSetComponent';
import autobind from '../_util/autobind';
let Button = class Button extends DataSetComponent {
    constructor(props, context) {
        super(props, context);
        this.handleClickWait = this.getHandleClick(props);
    }
    get loading() {
        const { type, dataSet, loading } = this.observableProps;
        return (loading ||
            (type === ButtonType.submit && !!dataSet && dataSet.status === DataSetStatus.submitting));
    }
    set loading(loading) {
        runInAction(() => {
            this.observableProps.loading = loading;
        });
    }
    getObservableProps(props, context) {
        let loading = false;
        if ('loading' in props) {
            loading = props.loading;
        }
        return {
            ...super.getObservableProps(props, context),
            dataSet: 'dataSet' in props ? props.dataSet : context.dataSet,
            loading,
            type: props.type,
        };
    }
    componentWillReceiveProps(nextProps, nextContext) {
        let loading = this.loading;
        if ('loading' in nextProps) {
            loading = nextProps.loading;
        }
        super.componentWillReceiveProps({
            ...nextProps,
            loading,
        }, nextContext);
        const { wait, waitType } = this.props;
        if (wait !== nextProps.wait || waitType !== nextProps.waitType) {
            this.handleClickWait = this.getHandleClick(nextProps);
        }
    }
    componentWillUnmount() {
        this.handleClickWait.cancel();
    }
    getHandleClick(props) {
        const { wait, waitType } = props;
        if (wait && waitType) {
            const options = { leading: true, trailing: true };
            if (waitType === WaitType.throttle) {
                options.trailing = false;
                options.maxWait = wait;
            }
            else if (waitType === WaitType.debounce) {
                options.leading = false;
            }
            return debounce(this.handleClick, wait, options);
        }
        return debounce(this.handleClick, 0);
    }
    handleClickIfBubble(e) {
        const { wait, waitType } = this.props;
        if (wait && waitType) {
            e.stopPropagation();
            this.handleClickWait(e);
        }
        else {
            this.handleClick(e);
        }
    }
    async handleClick(e) {
        const { onClick } = this.props;
        if (onClick) {
            const afterClick = onClick(e);
            if (isPromise(afterClick)) {
                try {
                    this.loading = true;
                    await afterClick;
                }
                finally {
                    this.loading = false;
                }
            }
        }
    }
    isDisabled() {
        const { disabled } = this.context;
        return disabled || super.isDisabled() || this.loading;
    }
    getOtherProps() {
        const otherProps = omit(super.getOtherProps(), [
            'icon',
            'funcType',
            'color',
            'loading',
            'wait',
            'waitType',
        ]);
        if (!this.isDisabled()) {
            otherProps.onClick = this.handleClickIfBubble;
        }
        return otherProps;
    }
    getClassName(...props) {
        const { prefixCls, props: { color = getConfig('buttonColor'), funcType = getConfig('buttonFuncType'), children, icon, }, } = this;
        const childrenCount = Children.count(children);
        return super.getClassName({
            [`${prefixCls}-${funcType}`]: funcType,
            [`${prefixCls}-${color}`]: color,
            [`${prefixCls}-icon-only`]: icon
                ? childrenCount === 0 || children === false
                : childrenCount === 1 && children.type && children.type.__C7N_ICON,
        }, ...props);
    }
    render() {
        const { children, icon, href } = this.props;
        const buttonIcon = this.loading ? (React.createElement(Progress, { key: "loading", type: ProgressType.loading, size: Size.small })) : (icon && React.createElement(Icon, { type: icon }));
        const hasString = Children.toArray(children).some(child => isString(child));
        const Cmp = href ? 'a' : 'button';
        const props = this.getMergedProps();
        return (React.createElement(Ripple, { disabled: this.isDisabled() },
            React.createElement(Cmp, Object.assign({}, (href ? omit(props, ['type']) : props)),
                buttonIcon,
                hasString ? React.createElement("span", null, children) : children)));
    }
};
Button.displayName = 'Button';
// eslint-disable-next-line camelcase
Button.__PRO_BUTTON = true;
Button.contextType = FormContext;
Button.propTypes = {
    /**
     * 按钮展现模式
     * 可选值：'flat' | 'raised'
     * @default raised
     */
    funcType: PropTypes.oneOf([FuncType.flat, FuncType.raised]),
    /**
     * 按钮颜色风格
     * 可选值：'default' | 'primary' | 'gray' | 'blue' | 'red' | 'green' | 'yellow' | 'purple' | 'dark'
     * @default 'default'
     */
    color: PropTypes.oneOf([
        ButtonColor.default,
        ButtonColor.primary,
        ButtonColor.gray,
        ButtonColor.blue,
        ButtonColor.red,
        ButtonColor.green,
        ButtonColor.yellow,
        ButtonColor.purple,
        ButtonColor.dark,
    ]),
    /**
     * 按钮类型
     * 可选值：'button' | 'submit' | 'reset'
     * @default 'button'
     */
    type: PropTypes.oneOf([ButtonType.button, ButtonType.submit, ButtonType.reset]),
    /**
     * 按钮是否是加载状态
     */
    loading: PropTypes.bool,
    /**
     * 点击跳转的地址，指定此属性 button 的行为和 a 链接一致
     */
    href: PropTypes.string,
    /**
     * 相当于 a 链接的 target 属性，href 存在时生效
     */
    target: PropTypes.string,
    /**
     * 点击等待时间
     */
    wait: PropTypes.number,
    /**
     * 点击间隔类型，可选值：throttle | debounce
     * @default throttle
     */
    waitType: PropTypes.oneOf([WaitType.throttle, WaitType.debounce]),
    ...DataSetComponent.propTypes,
};
Button.defaultProps = {
    suffixCls: 'btn',
    type: ButtonType.button,
    waitType: WaitType.throttle,
};
__decorate([
    computed
], Button.prototype, "loading", null);
__decorate([
    autobind
], Button.prototype, "handleClickIfBubble", null);
__decorate([
    autobind
], Button.prototype, "handleClick", null);
Button = __decorate([
    observer
], Button);
export default Button;
